import { useEffect, useState } from 'react';

/**
 * Returns the id of the landing page section currently crossing the middle of
 * the viewport, so the navigation can mark it as the current location.
 * Uses one IntersectionObserver (no scroll listener).
 */
export function useActiveSection(ids: readonly string[], enabled: boolean): string | null {
  const [active, setActive] = useState<string | null>(null);
  const key = ids.join('|');

  useEffect(() => {
    if (!enabled || !('IntersectionObserver' in window)) {
      setActive(null);
      return;
    }

    const elements = key
      .split('|')
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    // A thin band around the middle of the viewport acts as the trigger line.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { root: document, rootMargin: '-45% 0px -50% 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    // Above the first tracked section (the hero) nothing is current.
    const first = elements[0];
    const topObserver = first
      ? new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting && entry.boundingClientRect.top > 0) setActive(null);
        }, { root: document, rootMargin: '-45% 0px -50% 0px' })
      : null;
    if (first) topObserver?.observe(first);

    return () => {
      observer.disconnect();
      topObserver?.disconnect();
    };
  }, [key, enabled]);

  return active;
}
